import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useSeasonalTheme } from '../contexts/SeasonalThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

// --- SVG Shape Components ---

const CherryBlossom = ({ color = '#ff9eb5', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <g opacity="0.85">
      <ellipse cx="12" cy="6" rx="3.5" ry="5" fill={color} transform="rotate(0 12 12)" />
      <ellipse cx="12" cy="6" rx="3.5" ry="5" fill={color} transform="rotate(72 12 12)" />
      <ellipse cx="12" cy="6" rx="3.5" ry="5" fill={color} transform="rotate(144 12 12)" />
      <ellipse cx="12" cy="6" rx="3.5" ry="5" fill={color} transform="rotate(216 12 12)" />
      <ellipse cx="12" cy="6" rx="3.5" ry="5" fill={color} transform="rotate(288 12 12)" />
      <circle cx="12" cy="12" r="2.5" fill="#ffe066" />
    </g>
  </svg>
);

const Lantern = ({ color = '#e84040', size = 24 }) => (
  <svg width={size} height={size * 1.4} viewBox="0 0 24 34" fill="none">
    <rect x="9" y="0" width="6" height="4" rx="1" fill="#d4a017" />
    <line x1="12" y1="4" x2="12" y2="7" stroke="#d4a017" strokeWidth="1.5" />
    <ellipse cx="12" cy="18" rx="9" ry="11" fill={color} opacity="0.9" />
    <ellipse cx="12" cy="18" rx="9" ry="11" fill="none" stroke={color} strokeWidth="0.5" opacity="0.5" />
    <line x1="12" y1="7" x2="12" y2="29" stroke="#d4a017" strokeWidth="0.8" opacity="0.4" />
    <ellipse cx="12" cy="18" rx="6" ry="8" fill="none" stroke="#ffe066" strokeWidth="0.5" opacity="0.3" />
    <rect x="8" y="29" width="8" height="2" rx="1" fill="#d4a017" opacity="0.7" />
    <line x1="10" y1="31" x2="10" y2="34" stroke="#d4a017" strokeWidth="0.8" opacity="0.5" />
    <line x1="14" y1="31" x2="14" y2="34" stroke="#d4a017" strokeWidth="0.8" opacity="0.5" />
  </svg>
);

const Snowflake = ({ color = '#b0d8f0', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <g stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.8">
      <line x1="12" y1="2" x2="12" y2="22" />
      <line x1="3.5" y1="7" x2="20.5" y2="17" />
      <line x1="3.5" y1="17" x2="20.5" y2="7" />
      <line x1="12" y1="2" x2="10" y2="5" /><line x1="12" y1="2" x2="14" y2="5" />
      <line x1="12" y1="22" x2="10" y2="19" /><line x1="12" y1="22" x2="14" y2="19" />
      <line x1="3.5" y1="7" x2="6" y2="8.5" /><line x1="3.5" y1="7" x2="5.5" y2="5" />
      <line x1="20.5" y1="17" x2="18" y2="15.5" /><line x1="20.5" y1="17" x2="18.5" y2="19" />
      <line x1="3.5" y1="17" x2="5.5" y2="19" /><line x1="3.5" y1="17" x2="6" y2="15.5" />
      <line x1="20.5" y1="7" x2="18.5" y2="5" /><line x1="20.5" y1="7" x2="18" y2="8.5" />
    </g>
  </svg>
);

const Heart = ({ color = '#e84070', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} opacity="0.8">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const Star5 = ({ color = '#ffd700', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} opacity="0.85">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const Balloon = ({ color = '#e06030', size = 22 }) => (
  <svg width={size} height={size * 1.5} viewBox="0 0 22 33" fill="none">
    <ellipse cx="11" cy="11" rx="9" ry="11" fill={color} opacity="0.85" />
    <ellipse cx="9" cy="8" rx="2.5" ry="3" fill="white" opacity="0.2" />
    <polygon points="11,22 8,25 14,25" fill={color} opacity="0.7" />
    <path d="M11 25 Q9 28 11 31 Q13 28 11 25" stroke={color} strokeWidth="0.8" fill="none" opacity="0.5" />
  </svg>
);

const MoonCake = ({ color = '#d4880a', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <ellipse cx="12" cy="14" rx="10" ry="8" fill={color} opacity="0.85" />
    <ellipse cx="12" cy="12" rx="10" ry="8" fill={color} />
    <ellipse cx="12" cy="12" rx="10" ry="8" fill="none" stroke="#c47a08" strokeWidth="0.5" />
    <circle cx="12" cy="11" r="4" fill="#ffe066" opacity="0.5" />
    <path d="M8 11 Q12 8 16 11" stroke="#c47a08" strokeWidth="0.6" fill="none" opacity="0.5" />
    <path d="M8 13 Q12 10 16 13" stroke="#c47a08" strokeWidth="0.6" fill="none" opacity="0.5" />
  </svg>
);

const Firework = ({ color = '#ff6040', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <g stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.8">
      <line x1="12" y1="12" x2="12" y2="2" />
      <line x1="12" y1="12" x2="12" y2="22" />
      <line x1="12" y1="12" x2="2" y2="12" />
      <line x1="12" y1="12" x2="22" y2="12" />
      <line x1="12" y1="12" x2="5" y2="5" />
      <line x1="12" y1="12" x2="19" y2="5" />
      <line x1="12" y1="12" x2="5" y2="19" />
      <line x1="12" y1="12" x2="19" y2="19" />
    </g>
    <circle cx="12" cy="2" r="1.2" fill={color} opacity="0.8" />
    <circle cx="12" cy="22" r="1.2" fill={color} opacity="0.8" />
    <circle cx="2" cy="12" r="1.2" fill={color} opacity="0.8" />
    <circle cx="22" cy="12" r="1.2" fill={color} opacity="0.8" />
  </svg>
);

const GiftBox = ({ color = '#c41e3a', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="10" width="18" height="12" rx="2" fill={color} opacity="0.85" />
    <rect x="2" y="7" width="20" height="5" rx="1.5" fill={color} />
    <rect x="10.5" y="7" width="3" height="15" fill="#ffe066" opacity="0.6" />
    <rect x="2" y="8.5" width="20" height="2" fill="#ffe066" opacity="0.6" />
    <path d="M12 7 Q8 2 6 4 Q4 6 8 7" stroke="#ffe066" strokeWidth="1.2" fill="none" />
    <path d="M12 7 Q16 2 18 4 Q20 6 16 7" stroke="#ffe066" strokeWidth="1.2" fill="none" />
  </svg>
);

// --- SVG shape map per theme ---
const THEME_SHAPES = {
  tet: [
    (s) => <CherryBlossom size={s} color="#ff9eb5" />,
    (s) => <Lantern size={s} color="#e84040" />,
    (s) => <Star5 size={s} color="#ffd700" />,
    (s) => <CherryBlossom size={s} color="#ffb8c8" />,
    (s) => <Lantern size={s} color="#cc3030" />,
  ],
  valentine: [
    (s) => <Heart size={s} color="#e84070" />,
    (s) => <Heart size={s} color="#ff6090" />,
    (s) => <Heart size={s} color="#d03060" />,
    (s) => <Star5 size={s} color="#ffa0b8" />,
    (s) => <CherryBlossom size={s} color="#ff88a8" />,
  ],
  reunification: [
    (s) => <Star5 size={s} color="#ffd700" />,
    (s) => <Firework size={s} color="#cc0000" />,
    (s) => <Star5 size={s} color="#ff4040" />,
    (s) => <Firework size={s} color="#e6b800" />,
  ],
  children: [
    (s) => <Balloon size={s} color="#e06030" />,
    (s) => <Balloon size={s} color="#40a0e0" />,
    (s) => <Balloon size={s} color="#e0457b" />,
    (s) => <Star5 size={s} color="#ffd700" />,
    (s) => <Balloon size={s} color="#50b860" />,
  ],
  midAutumn: [
    (s) => <Lantern size={s} color="#e8a020" />,
    (s) => <MoonCake size={s} color="#d4880a" />,
    (s) => <Star5 size={s} color="#ffd700" />,
    (s) => <Lantern size={s} color="#d07010" />,
    (s) => <Star5 size={s} color="#f0c040" />,
  ],
  nationalDay: [
    (s) => <Star5 size={s} color="#ffd700" />,
    (s) => <Firework size={s} color="#cc0000" />,
    (s) => <Star5 size={s} color="#ff4040" />,
    (s) => <Firework size={s} color="#ffd700" />,
  ],
  christmas: [
    (s) => <Snowflake size={s} color="#b0d8f0" />,
    (s) => <Star5 size={s} color="#ffd700" />,
    (s) => <GiftBox size={s} color="#c41e3a" />,
    (s) => <Snowflake size={s} color="#d0e8f8" />,
    (s) => <GiftBox size={s} color="#1a7a3a" />,
  ],
  spring: [
    (s) => <CherryBlossom size={s} color="#f0a0b8" />,
    (s) => <CherryBlossom size={s} color="#e888a0" />,
    (s) => <Star5 size={s} color="#90d060" />,
    (s) => <CherryBlossom size={s} color="#ffb8c8" />,
    (s) => <Heart size={s} color="#d4567a" />,
  ],
  summer: [
    (s) => <Star5 size={s} color="#ffd700" />,
    (s) => <Balloon size={s} color="#0098b8" />,
    (s) => <Star5 size={s} color="#e08820" />,
    (s) => <Balloon size={s} color="#40b8d0" />,
    (s) => <Star5 size={s} color="#f0c040" />,
  ],
  autumn: [
    (s) => <CherryBlossom size={s} color="#c06020" />,
    (s) => <CherryBlossom size={s} color="#d08030" />,
    (s) => <CherryBlossom size={s} color="#a04830" />,
    (s) => <Star5 size={s} color="#e0a040" />,
    (s) => <CherryBlossom size={s} color="#b85020" />,
  ],
  winter: [
    (s) => <Snowflake size={s} color="#90c8e8" />,
    (s) => <Snowflake size={s} color="#b0d8f0" />,
    (s) => <Snowflake size={s} color="#a0c0d8" />,
    (s) => <Star5 size={s} color="#c0d8f0" />,
    (s) => <Snowflake size={s} color="#d0e8f8" />,
  ],
};

// --- Banner text per theme ---
const THEME_BANNERS = {
  tet:           { en: 'Happy Lunar New Year!', vi: 'Ch\u00FAc M\u1EEBng N\u0103m M\u1EDBi!' },
  valentine:     { en: "Happy Valentine's Day!", vi: 'Ch\u00FAc M\u1EEBng Ng\u00E0y T\u00ECnh Nh\u00E2n!' },
  reunification: { en: 'Happy Reunification Day!', vi: 'M\u1EEBng Ng\u00E0y Gi\u1EA3i Ph\u00F3ng!' },
  children:      { en: "Happy Children's Day!", vi: 'Ch\u00FAc M\u1EEBng Ng\u00E0y Qu\u1ED1c T\u1EBF Thi\u1EBFu Nhi!' },
  midAutumn:     { en: 'Happy Mid-Autumn Festival!', vi: 'Ch\u00FAc M\u1EEBng T\u1EBFt Trung Thu!' },
  nationalDay:   { en: 'Happy National Day!', vi: 'Ch\u00FAc M\u1EEBng Qu\u1ED1c Kh\u00E1nh!' },
  christmas:     { en: 'Merry Christmas!', vi: 'Ch\u00FAc M\u1EEBng Gi\u00E1ng Sinh!' },
  spring:        { en: 'Welcome Spring!', vi: 'Ch\u00E0o M\u00F9a Xu\u00E2n!' },
  summer:        { en: 'Hello Summer!', vi: 'Ch\u00E0o M\u00F9a H\u00E8!' },
  autumn:        { en: 'Welcome Autumn!', vi: 'Ch\u00E0o M\u00F9a Thu!' },
  winter:        { en: 'Hello Winter!', vi: 'Ch\u00E0o M\u00F9a \u0110\u00F4ng!' },
};

// --- Animations ---

const floatDown = keyframes`
  0% { transform: translateY(-5vh) translateX(0) rotate(0deg); opacity: 0; }
  5% { opacity: 0.85; }
  25% { transform: translateY(25vh) translateX(10px) rotate(90deg); }
  50% { transform: translateY(50vh) translateX(-8px) rotate(180deg); }
  75% { transform: translateY(75vh) translateX(12px) rotate(270deg); }
  90% { opacity: 0.85; }
  100% { transform: translateY(105vh) translateX(0) rotate(360deg); opacity: 0; }
`;

const shimmer = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

const bannerScroll = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
`;

const hover = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-12px) rotate(3deg); }
  50% { transform: translateY(-4px) rotate(-2deg); }
  75% { transform: translateY(-16px) rotate(2deg); }
`;

const hoverSway = keyframes`
  0%, 100% { transform: translateX(0); }
  33% { transform: translateX(8px); }
  66% { transform: translateX(-6px); }
`;

// --- Styled Components ---

const ParticlesContainer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9998;
  overflow: hidden;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const ParticleWrap = styled.div`
  position: absolute;
  top: -40px;
  left: ${p => p.$left}%;
  animation: ${floatDown} ${p => p.$duration}s linear infinite ${p => p.$delay}s;
  will-change: transform, opacity;
  opacity: 0;
  filter: ${p => p.$blur ? 'blur(1px)' : 'none'};
`;

const Banner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 28px;
  z-index: 1001;
  background: linear-gradient(
    90deg,
    var(--brand-primary) 0%,
    var(--brand-secondary) 50%,
    var(--brand-primary) 100%
  );
  display: flex;
  align-items: center;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--brand-shadow);

  @media (prefers-reduced-motion: reduce) {
    justify-content: center;
  }
`;

const BannerText = styled.span`
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0,0,0,0.3);
  letter-spacing: 0.5px;
  animation: ${bannerScroll} 18s linear infinite;
  padding: 0 60px;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    padding: 0;
  }
`;

const BannerIcon = styled.span`
  margin: 0 12px;
  animation: ${shimmer} 2s ease-in-out infinite;
`;


const FloatingContainer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9997;
  overflow: hidden;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const FloatingElement = styled.div`
  position: absolute;
  top: ${p => p.$top}%;
  ${p => p.$side === 'left' ? `left: ${p.$offset}px;` : `right: ${p.$offset}px;`}
  animation:
    ${hover} ${p => p.$duration}s ease-in-out infinite ${p => p.$delay}s,
    ${hoverSway} ${p => p.$swayDuration}s ease-in-out infinite ${p => p.$delay + 1}s;
  opacity: ${p => p.$opacity};
  filter: ${p => p.$blur ? 'blur(1.5px)' : 'none'};
  will-change: transform;
`;

// --- Hovering element configs per theme ---
const THEME_FLOATERS = {
  tet: [
    { shapeIdx: 1, top: 20, side: 'left', offset: 15, size: 32, opacity: 0.6 },
    { shapeIdx: 0, top: 45, side: 'right', offset: 10, size: 24, opacity: 0.5 },
    { shapeIdx: 1, top: 70, side: 'left', offset: 8, size: 28, opacity: 0.55 },
    { shapeIdx: 0, top: 85, side: 'right', offset: 20, size: 20, opacity: 0.4 },
  ],
  valentine: [
    { shapeIdx: 0, top: 18, side: 'right', offset: 12, size: 22, opacity: 0.5 },
    { shapeIdx: 1, top: 40, side: 'left', offset: 10, size: 18, opacity: 0.45 },
    { shapeIdx: 2, top: 65, side: 'right', offset: 18, size: 24, opacity: 0.5 },
    { shapeIdx: 0, top: 82, side: 'left', offset: 14, size: 20, opacity: 0.4 },
  ],
  reunification: [
    { shapeIdx: 0, top: 22, side: 'left', offset: 12, size: 26, opacity: 0.55 },
    { shapeIdx: 1, top: 55, side: 'right', offset: 10, size: 22, opacity: 0.5 },
    { shapeIdx: 0, top: 78, side: 'left', offset: 16, size: 20, opacity: 0.45 },
  ],
  children: [
    { shapeIdx: 0, top: 15, side: 'left', offset: 8, size: 28, opacity: 0.55 },
    { shapeIdx: 1, top: 35, side: 'right', offset: 12, size: 24, opacity: 0.5 },
    { shapeIdx: 2, top: 55, side: 'left', offset: 15, size: 22, opacity: 0.5 },
    { shapeIdx: 4, top: 75, side: 'right', offset: 10, size: 26, opacity: 0.55 },
  ],
  midAutumn: [
    { shapeIdx: 0, top: 20, side: 'right', offset: 10, size: 30, opacity: 0.55 },
    { shapeIdx: 1, top: 50, side: 'left', offset: 12, size: 24, opacity: 0.5 },
    { shapeIdx: 0, top: 75, side: 'right', offset: 15, size: 26, opacity: 0.5 },
  ],
  nationalDay: [
    { shapeIdx: 0, top: 25, side: 'left', offset: 12, size: 26, opacity: 0.55 },
    { shapeIdx: 1, top: 60, side: 'right', offset: 10, size: 22, opacity: 0.5 },
    { shapeIdx: 0, top: 80, side: 'left', offset: 16, size: 20, opacity: 0.45 },
  ],
  christmas: [
    { shapeIdx: 0, top: 15, side: 'left', offset: 10, size: 24, opacity: 0.5 },
    { shapeIdx: 2, top: 35, side: 'right', offset: 14, size: 26, opacity: 0.55 },
    { shapeIdx: 0, top: 55, side: 'left', offset: 8, size: 20, opacity: 0.45 },
    { shapeIdx: 4, top: 78, side: 'right', offset: 12, size: 28, opacity: 0.55 },
  ],
  spring: [
    { shapeIdx: 0, top: 18, side: 'right', offset: 12, size: 24, opacity: 0.5 },
    { shapeIdx: 2, top: 42, side: 'left', offset: 10, size: 22, opacity: 0.5 },
    { shapeIdx: 4, top: 65, side: 'right', offset: 15, size: 20, opacity: 0.45 },
    { shapeIdx: 0, top: 85, side: 'left', offset: 8, size: 26, opacity: 0.5 },
  ],
  summer: [
    { shapeIdx: 0, top: 20, side: 'left', offset: 10, size: 28, opacity: 0.55 },
    { shapeIdx: 1, top: 48, side: 'right', offset: 12, size: 24, opacity: 0.5 },
    { shapeIdx: 3, top: 72, side: 'left', offset: 14, size: 22, opacity: 0.45 },
  ],
  autumn: [
    { shapeIdx: 0, top: 16, side: 'left', offset: 10, size: 26, opacity: 0.55 },
    { shapeIdx: 1, top: 38, side: 'right', offset: 14, size: 22, opacity: 0.5 },
    { shapeIdx: 2, top: 58, side: 'left', offset: 8, size: 24, opacity: 0.5 },
    { shapeIdx: 3, top: 80, side: 'right', offset: 12, size: 20, opacity: 0.45 },
  ],
  winter: [
    { shapeIdx: 0, top: 14, side: 'right', offset: 10, size: 22, opacity: 0.5 },
    { shapeIdx: 1, top: 36, side: 'left', offset: 12, size: 26, opacity: 0.55 },
    { shapeIdx: 2, top: 60, side: 'right', offset: 8, size: 20, opacity: 0.45 },
    { shapeIdx: 0, top: 82, side: 'left', offset: 14, size: 24, opacity: 0.5 },
  ],
};

// --- Main Component ---

const SeasonalDecorations = () => {
  const { activeTheme, bannerText: customBanner } = useSeasonalTheme();
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  const particles = useMemo(() => {
    if (!activeTheme) return [];

    const shapes = THEME_SHAPES[activeTheme.id] || [];
    if (shapes.length === 0) return [];

    return Array.from({ length: activeTheme.particleCount }, (_, i) => ({
      id: i,
      shapeIdx: i % shapes.length,
      left: Math.random() * 94 + 3,
      size: 16 + Math.random() * 16,
      duration: 10 + Math.random() * 12,
      delay: Math.random() * 14,
      blur: Math.random() > 0.75,
    }));
  }, [activeTheme]);

  if (!activeTheme || isAdmin) return null;

  const shapes = THEME_SHAPES[activeTheme.id] || [];
  const defaultBanner = THEME_BANNERS[activeTheme.id];
  const lang = currentLanguage === 'vi' ? 'vi' : 'en';
  // Use custom banner if set, otherwise fall back to default theme banner
  const displayText = (customBanner?.[lang]?.trim()) || defaultBanner?.[lang] || '';

  return (
    <>
      {/* Seasonal banner strip */}
      <Banner aria-hidden="true">
        <BannerText>
          <BannerIcon>{activeTheme.icon}</BannerIcon>
          {displayText}
          <BannerIcon>{activeTheme.icon}</BannerIcon>
          {displayText}
          <BannerIcon>{activeTheme.icon}</BannerIcon>
          {displayText}
          <BannerIcon>{activeTheme.icon}</BannerIcon>
        </BannerText>
      </Banner>

      {/* Hovering elements on sides */}
      <FloatingContainer aria-hidden="true">
        {(THEME_FLOATERS[activeTheme.id] || []).map((f, i) => (
          <FloatingElement
            key={`float-${i}`}
            $top={f.top}
            $side={f.side}
            $offset={f.offset}
            $opacity={f.opacity}
            $blur={f.blur}
            $duration={6 + i * 1.5}
            $delay={i * 2}
            $swayDuration={4 + i}
          >
            {shapes[f.shapeIdx % shapes.length](f.size)}
          </FloatingElement>
        ))}
      </FloatingContainer>

      {/* Falling SVG particles */}
      <ParticlesContainer aria-hidden="true">
        {particles.map(p => (
          <ParticleWrap
            key={p.id}
            $left={p.left}
            $duration={p.duration}
            $delay={p.delay}
            $blur={p.blur}
          >
            {shapes[p.shapeIdx](p.size)}
          </ParticleWrap>
        ))}
      </ParticlesContainer>
    </>
  );
};

export default SeasonalDecorations;
